import { IDevice } from '@pages/Assets/Inventory/types';
import { Alert, Form, FormInstance, Radio, Select } from 'antd';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import ActionTypes from '@utils/deviceActions';
import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import {
  DeviceActionProps,
  DeviceActionStepsProps,
} from '@components/Actions/ActionsModal/types';
import rolesConstants from '@utils/rolesConstants';

interface ChangeAPNProps {
  devices: IDevice[];
  isUpload?: boolean;
  form: FormInstance<any>;
}

interface ChangeAPNPayloadProps {
  action: ActionTypes.APN_ADD | ActionTypes.APN_REMOVE;
  devices: {
    msisdn: string;
    processData: string;
  }[];
}

const useChangeApn = ({
  devices,
  isUpload,
  form,
}: ChangeAPNProps): DeviceActionProps => {
  const { t } = useTranslation(['device-actions-modal']);
  const { keycloak } = useContext(KeycloakContext);
  const actionType = Form.useWatch('actionType', form);
  const apnSelected = Form.useWatch('apnSelected', form);

  const { data: apnsList } = useSwr<string[]>(
    `/service-proxy/apns-customer/${keycloak?.idTokenParsed?.customerId}`,
  );

  const apnOptions = useMemo(() => {
    const allApns = apnsList?.map(apn => ({
      label: apn,
      value: apn,
    }));

    if (actionType === ActionTypes.APN_ADD) {
      return allApns;
    }

    return [
      {
        label: t('steps.change-apn.allApns'),
        value: 'ALL',
      },
      {
        label: t('steps.change-apn.apnsList'),
        options: allApns,
      },
    ];
  }, [actionType, apnsList, t]);

  const steps: DeviceActionStepsProps[] = useMemo(() => {
    const defaultSteps: DeviceActionStepsProps[] = [
      {
        title: t('steps.change-apn.action'),
        name: 'actionType',
        showWhenUpload: true,
        content: (
          <Radio.Group optionType="button">
            <Radio.Button value={ActionTypes.APN_ADD}>
              <PlusCircleOutlined style={{ marginRight: '5px' }} />
              {t('steps.change-apn.add')}
            </Radio.Button>
            <Radio.Button value={ActionTypes.APN_REMOVE}>
              <MinusCircleOutlined style={{ marginRight: '5px' }} />
              {t('steps.change-apn.remove')}
            </Radio.Button>
          </Radio.Group>
        ),
      },
    ];

    if (!isUpload) {
      defaultSteps.push({
        title: t('steps.change-apn.select-apn'),
        name: 'apnSelected',
        showWhenUpload: false,
        content: (
          <Select
            showSearch
            style={{ width: '100%', marginTop: 8 }}
            optionFilterProp="label"
            options={apnOptions}
          />
        ),
      });
    }

    return defaultSteps;
  }, [t, isUpload, apnOptions]);

  const payloadToSend = useMemo(
    (): ChangeAPNPayloadProps => ({
      action: actionType,
      devices: devices?.map(device => ({
        msisdn: device.msisdn,
        processData: isUpload ? device.apn : apnSelected,
      })),
    }),
    [actionType, devices, apnSelected, isUpload],
  );

  return {
    payloadToSend,
    title: t(`device-actions:labels.mot.${rolesConstants.CHANGE_APN}`),
    steps,
    uploadPosition: 1,
    uploadCustomInfo: actionType === ActionTypes.APN_REMOVE && (
      <Alert
        showIcon
        description={t('steps.change-apn.remove-all-apns-help')}
      />
    ),
  };
};
export default useChangeApn;
